import type { CommentItem, CommentReply } from '@/lib/api/comments';
import { sortByDate } from '@/lib/utils/sortByDate';

/** Author of the comment a reply answers; shown as the @Name chip. */
export interface ReplyTarget {
  author: string;
  outpollUserId: string;
}

export interface ThreadedComment extends CommentReply {
  replies: ThreadedComment[];
  replyTo: ReplyTarget | null;
}

function findRoot(item: CommentItem, byId: Map<string, CommentItem>): CommentItem {
  const seen = new Set<string>();
  let current = item;
  while (current.parent && byId.has(current.parent) && !seen.has(current.id)) {
    seen.add(current.id);
    current = byId.get(current.parent)!;
  }
  return current;
}

/**
 * API returns comments flat; a reply's `parent` may be another reply. Like
 * Polymarket, threads are two levels: every descendant sits flat under its
 * root, oldest first, and keeps whose comment it answers in `replyTo`.
 * A comment whose parent isn't loaded becomes a root.
 */
export function buildThreadedComments(flat: CommentItem[]): ThreadedComment[] {
  const byId = new Map<string, CommentItem>();
  for (const item of flat) {
    if (!byId.has(item.id)) byId.set(item.id, item);
  }

  const roots = new Map<string, ThreadedComment>();
  const replies = new Map<string, ThreadedComment[]>();
  for (const item of byId.values()) {
    const root = findRoot(item, byId);
    const parent = item.parent ? byId.get(item.parent) : undefined;
    const isRoot = root.id === item.id;
    const node: ThreadedComment = {
      ...item,
      replies: [],
      replyTo:
        !isRoot && parent ? { author: parent.author, outpollUserId: parent.outpollUserId } : null,
    };
    if (isRoot) {
      roots.set(item.id, node);
    } else if (replies.has(root.id)) {
      replies.get(root.id)!.push(node);
    } else {
      replies.set(root.id, [node]);
    }
  }

  return [...roots.values()].map((root) => ({
    ...root,
    replies: sortByDate(replies.get(root.id) ?? [], 'createdAt', 'asc'),
  }));
}
